// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The four-step onboarding strip (G2-11; 08 §3, and the conventions' "onboarding progress bar").
//
// > *Play a ball → make a ramp → start a mode → save your score.*
//
// **Four steps, and the fourth is the one the strip exists for.** The first three happen to
// everybody who plays; the fourth is where an anonymous player finds out that a score can be kept,
// and it is the only step that is a *reason to make an account* rather than a thing that has
// already happened. The strip is the goal gradient the conventions ask for, and a goal gradient
// with nothing at the end of it is a progress bar.
//
// **Never drawn over the machine on a first visit**, which is G2-11's own constraint and worth
// stating as a rule rather than as a layout note: 08 §1's first thirty seconds are *"NEON NOVA in
// attract mode, lights running"* and a single primary control. A tutorial strip laid over that is a
// product explaining itself before it has done anything, and it is exactly what a visitor closes.
// So it appears once the first step is *done* — after a ball has been played — which also makes the
// strip's first appearance a reward rather than an instruction.
//
// **It retires permanently**, like the touch-zone hint and for the same reason: a player who has
// saved a score does not need to be told they can. And it is clearable from Settings, like the zone
// hint, because "permanently" was too strong there once already.
//
// No React here. The steps and their transitions are decisions, and the strip that draws them is
// four lines of JSX over this.

import { readLocal, removeLocal, safeStorage, writeLocal, type StorageLike } from '../storage/local'

/**
 * The four steps, in order.
 *
 * Ordered by when they happen rather than by importance, because that is what a progress strip
 * claims: a player looking at it should be able to point at where they are.
 */
export const ONBOARDING_STEPS = ['playBall', 'makeRamp', 'startMode', 'saveScore'] as const
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number]

/**
 * One key for the whole strip, not one per step.
 *
 * **The opposite of `firstRun.ts`'s choice, and the difference is what the flags are for.** There,
 * the zone hint and the plunger arrow are two lessons learned independently — "a thumb finds the
 * flippers in seconds and the plunger's *drag* is the one thing nobody guesses" — so they retire
 * separately. Here the four steps are one journey with an order, and the only questions anybody
 * asks are "how far along" and "is it finished"; four keys would be four ways to store a number
 * between zero and four.
 */
export const ONBOARDING_KEY = 'hcc.pinball.onboarding'

/** What has been done, as a set. */
export type OnboardingProgress = ReadonlySet<OnboardingStep>

const isStep = (value: string): value is OnboardingStep =>
  (ONBOARDING_STEPS as readonly string[]).includes(value)

/**
 * What this browser has done, and whether the strip is finished with.
 *
 * Stored as the comma-joined step names rather than as a count, so a step inserted into the middle
 * of the list later does not silently mark itself done for everybody who had passed the old step
 * three. Unknown names are dropped, which is what a newer client's storage looks like to an older
 * one.
 */
export function readOnboarding(storage: StorageLike | undefined = safeStorage()): OnboardingProgress {
  const stored = readLocal(storage, ONBOARDING_KEY)
  if (stored === undefined || stored === '') return new Set()
  return new Set(stored.split(',').filter(isStep))
}

/**
 * Record a step, and answer what the progress is now.
 *
 * Idempotent: the events that mark these are game events, and a player makes a ramp many times a
 * ball. Writing on every one of them would be a `localStorage` write per shot.
 */
export function markOnboarding(
  step: OnboardingStep,
  storage: StorageLike | undefined = safeStorage(),
): OnboardingProgress {
  const held = readOnboarding(storage)
  if (held.has(step)) return held
  const next = new Set(held)
  next.add(step)
  // Written in the canonical order rather than in the order they happened, so two browsers that
  // did the same four things hold the same string — which is what makes a stored value comparable
  // in a test and readable in a devtools panel.
  writeLocal(storage, ONBOARDING_KEY, ONBOARDING_STEPS.filter((s) => next.has(s)).join(','))
  return next
}

/** Show the strip again from the beginning. The Settings control, like the zone hint's. */
export function forgetOnboarding(storage: StorageLike | undefined = safeStorage()): void {
  removeLocal(storage, ONBOARDING_KEY)
}

/** Everything done. The strip retires and is not drawn again. */
export function onboardingComplete(progress: OnboardingProgress): boolean {
  return ONBOARDING_STEPS.every((step) => progress.has(step))
}

/**
 * Should the strip be on screen at all?
 *
 * **Not before the first step is done**, which is G2-11's constraint and 08 §1's: a tutorial laid
 * over a machine a visitor has not yet touched is a product explaining itself before it has done
 * anything. The first appearance is therefore a reward — one step lit of four — rather than a list
 * of homework.
 *
 * **And not after the last**, permanently. A player who has saved a score does not need to be told
 * they can.
 */
export function showOnboarding(progress: OnboardingProgress): boolean {
  return progress.size > 0 && !onboardingComplete(progress)
}

/** How far along, 0…1. The goal gradient the conventions ask for, as a number a bar can draw. */
export function onboardingFraction(progress: OnboardingProgress): number {
  return progress.size / ONBOARDING_STEPS.length
}

/**
 * The step to point at next, or nothing when they are all done.
 *
 * In declaration order rather than "the easiest one left": the strip claims an order, and a strip
 * that highlighted step four while step two was undone would be claiming a different one.
 */
export function nextOnboardingStep(progress: OnboardingProgress): OnboardingStep | undefined {
  return ONBOARDING_STEPS.find((step) => !progress.has(step))
}
